import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsApi } from '@/api/ops';
import type {
  BasisValue,
  ComputeShare,
  DatabaseFootprint,
  Footprint,
  LivePanel,
  Products,
  TrendPoint,
} from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Segmented } from '@/components/Select';
import { TrendChart } from '@/components/TrendChart';
import { BasisBadge, StatusChip } from '@/components/ops/Badges';
import { CreditAmount } from '@/components/ops/CreditAmount';
import { Gap, Section } from '@/components/ops/Layout';
import { MonthSelect } from '@/components/ops/Pickers';
import { CodeChip, QueryState, UnavailablePanel, describeUpstreamCode } from '@/components/ops/Problem';
import { RatedLinesTable } from '@/components/ops/RatedLines';
import { formatCredits } from '@/ops/credits';
import { currentPeriod, formatBytes, formatMs, formatNumber, formatRatio, formatTimestamp, niceBytesCeiling } from '@/ops/format';
import { LIVE, REPLICA } from '@/ops/live';
import { useCreditScale } from '@/ops/useCreditScale';
import { formatWhen } from '@/pages/AccountsPage';

const DAY_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

/**
 * Plan §5.4 — what one tenant costs the platform in resources. Storage, database activity and traffic
 * are MEASURED; the compute share is ATTRIBUTED and wears "estimated" on every number (CP3). Everything
 * but the products panel comes from the replica and renders with the platform down.
 */
export function UsageFootprintTab({ env, tenantId }: { env: string; tenantId: string }) {
  const [days, setDays] = useState(30);
  const footprint = useQuery({
    queryKey: ['ops', 'footprint', env, tenantId, days],
    queryFn: () => opsApi.tenants.footprint(env, tenantId, days),
    ...REPLICA,
  });

  return (
    <div className="stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <Segmented label="Period" inline value={days} onChange={setDays} options={DAY_OPTIONS} />
        {footprint.data && (
          <span className="t-caption muted">
            replica synced {formatWhen(footprint.data.synced_at)} · generated {formatTimestamp(footprint.data.generated_at)}
          </span>
        )}
      </div>

      <QueryState query={footprint}>
        {(data) => (
          <>
            <StoragePanel data={data} />
            <ActivityPanel data={data} />
            <TrafficPanel data={data} />
            <ComputeSharePanel share={data.compute_share} />
          </>
        )}
      </QueryState>

      {hasPermission(PERMISSIONS.costsRead) && <ProductsPanel env={env} tenantId={tenantId} />}
    </div>
  );
}

/**
 * The database total for a day exists only when EVERY database of the tenant has a sample that day. A
 * partial sum would draw a dip that is really a missing collection — so a partial day is a gap.
 */
export function totalDbTrend(databases: DatabaseFootprint[]): TrendPoint[] {
  const first = databases[0];
  if (!first) return [];
  return first.size_trend.map((point, index) => {
    let total = 0;
    for (const db of databases) {
      const value = db.size_trend[index]?.value;
      if (value === null || value === undefined) return { day: point.day, value: null };
      total += value;
    }
    return { day: point.day, value: total };
  });
}

function StoragePanel({ data }: { data: Footprint }) {
  const storage = data.storage;
  const dbTrend = useMemo(() => totalDbTrend(storage.databases), [storage.databases]);

  return (
    <Section title="Storage" actions={<BasisBadge basis={storage.basis} />} meta="bytes by database + object storage">
      <div className="kpi-row">
        <div className="card stat-tile">
          <div className="stat-label t-label">Databases (total)</div>
          <div className="stat-value">{storage.total_db_bytes === null ? <Gap why="No database size collected" /> : formatBytes(storage.total_db_bytes)}</div>
          <div className="stat-sub t-caption">{storage.databases.length} database(s)</div>
        </div>
        <div className="card stat-tile">
          <div className="stat-label t-label">Object storage</div>
          <div className="stat-value">{storage.objects.bytes === null ? <Gap why="Object storage not collected" /> : formatBytes(storage.objects.bytes)}</div>
          <div className="stat-sub t-caption">
            {storage.objects.file_count === null ? 'file count not collected' : `${formatNumber(storage.objects.file_count)} files`}
          </div>
        </div>
      </div>

      {/* Stacked full width, never side by side: the chart's text scales with its box, and two
          different magnitudes (databases vs objects) on one axis would flatten the smaller line. */}
      <div className="stack" style={{ marginTop: 16 }}>
        <div>
          <div className="t-label muted" style={{ marginBottom: 8 }}>Database bytes per day (a day missing any database is a gap)</div>
          <TrendChart
            height={200}
            series={[{ key: 'db', label: 'Database bytes', color: 'var(--series-1)', points: dbTrend.map((p) => ({ date: p.day, value: p.value })) }]}
            formatValue={formatBytes}
            niceMax={niceBytesCeiling}
          />
        </div>
        <div>
          <div className="t-label muted" style={{ marginBottom: 8 }}>Object bytes per day</div>
          <TrendChart
            height={200}
            series={[{
              key: 'objects',
              label: 'Object bytes',
              color: 'var(--series-1)',
              points: storage.objects.bytes_trend.map((p) => ({ date: p.day, value: p.value })),
            }]}
            formatValue={formatBytes}
            niceMax={niceBytesCeiling}
          />
        </div>
      </div>

      {storage.databases.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 16 }}>
          <table className="table">
            <thead>
              <tr><th>Database</th><th>Kind</th><th className="num">Size</th><th className="num">Connections</th><th>Sampled</th><th>Largest relations</th></tr>
            </thead>
            <tbody>
              {storage.databases.map((db) => (
                <tr key={db.name}>
                  <td className="mono">{db.name}</td>
                  <td className="mono t-caption">{db.kind}</td>
                  <td className="num">{db.size_bytes === null ? <Gap why="Not collected" /> : formatBytes(db.size_bytes)}</td>
                  <td className="num">{db.connections === null ? <Gap why="Not collected" /> : db.connections}</td>
                  <td className="t-caption">{db.sampled_at ? formatWhen(db.sampled_at) : '—'}</td>
                  <td className="t-caption">
                    {db.top_relations && db.top_relations.length > 0 ? (
                      <details>
                        <summary>{db.top_relations.length} relation(s)</summary>
                        <ul className="problem-list">
                          {db.top_relations.map((r) => <li key={r.name}><span className="mono">{r.name}</span> {formatBytes(r.bytes)}</li>)}
                        </ul>
                      </details>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function ActivityPanel({ data }: { data: Footprint }) {
  const days = data.database_activity.days;
  // An incomplete day (a database without both end points) is a partial sum — plotted as a GAP, listed
  // with its "partial" flag in the table below.
  const commits = days.map((d) => ({ date: d.day, value: d.complete ? d.xact_commit : null }));
  const incomplete = days.filter((d) => !d.complete).length;

  return (
    <Section title="Database activity" actions={<BasisBadge basis={data.database_activity.basis} />}
      meta="per-day deltas of pg_stat_database levels; a counter reset is a reset, never a negative day">
      <div className="t-label muted" style={{ marginBottom: 8 }}>
        Transactions committed per day{incomplete > 0 && ` · ${incomplete} incomplete day(s) drawn as gaps`}
      </div>
      <TrendChart height={200} series={[{ key: 'commits', label: 'Commits', color: 'var(--series-1)', points: commits }]} />
      <details className="viz-table">
        <summary>Daily detail</summary>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Day</th><th className="num">Commits</th><th className="num">Rollbacks</th><th className="num">Cache hit</th>
                <th className="num">Rows ins · upd · del</th><th className="num">Temp</th><th className="num">Active time</th>
              </tr>
            </thead>
            <tbody>
              {[...days].reverse().map((d) => (
                <tr key={d.day}>
                  <td className="t-caption">{d.day}{!d.complete && <> <StatusChip status="partial" tone="warning" title="At least one database has no sample at one end of this day." /></>}</td>
                  <td className="num">{formatNumber(d.xact_commit)}</td>
                  <td className="num">{formatNumber(d.xact_rollback)}</td>
                  <td className="num">{formatRatio(d.cache_hit_ratio, 2)}</td>
                  <td className="num t-caption">{formatNumber(d.tup_inserted)} · {formatNumber(d.tup_updated)} · {formatNumber(d.tup_deleted)}</td>
                  <td className="num">{formatBytes(d.temp_bytes)}</td>
                  <td className="num">{d.active_time_ms === null ? '—' : formatMs(d.active_time_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </Section>
  );
}

function TrafficPanel({ data }: { data: Footprint }) {
  const traffic = data.traffic;
  if (!traffic.available) {
    return (
      <Section title="Traffic" actions={<BasisBadge basis={traffic.basis} />}>
        <div className="empty-state t-body">
          The gateway scrape has never delivered traffic for this environment — requests, p95 and error rates are not
          collected yet (not zero).
        </div>
      </Section>
    );
  }

  return (
    <Section title="Traffic" actions={<BasisBadge basis={traffic.basis} />}
      meta="this tenant's own p95 and error rates — never averaged across tenants">
      <div className="stack">
        <div>
          <div className="t-label muted" style={{ marginBottom: 8 }}>Requests per day</div>
          <TrendChart height={200} series={[{
            key: 'requests', label: 'Requests', color: 'var(--series-1)',
            points: traffic.days.map((d) => ({ date: d.day, value: d.requests })),
          }]} />
        </div>
        <div>
          {/* A separate chart, never a second axis: latency and volume are different units. */}
          <div className="t-label muted" style={{ marginBottom: 8 }}>p95 latency per day (no requests = gap)</div>
          <TrendChart height={200} formatValue={(v) => formatMs(v)} series={[{
            key: 'p95', label: 'p95', color: 'var(--series-1)',
            points: traffic.days.map((d) => ({ date: d.day, value: d.p95_ms })),
          }]} />
        </div>
      </div>
      <p className="t-caption muted" style={{ margin: '8px 0 0' }}>
        A day with no gateway rows for this tenant shows 0 requests and no p95.
      </p>

      <div className="table-wrap" style={{ marginTop: 16 }}>
        <table className="table">
          <thead>
            <tr><th>App</th><th className="num">Requests</th><th className="num">4xx</th><th className="num">5xx</th><th className="num">5xx rate</th><th className="num">p95</th></tr>
          </thead>
          <tbody>
            {traffic.by_app.length === 0 && (
              <tr><td colSpan={6} className="empty-state">No requests from this tenant in the period.</td></tr>
            )}
            {traffic.by_app.map((a) => (
              <tr key={a.route_prefix}>
                <td><span className="mono">{a.app}</span><div className="t-caption muted mono">{a.route_prefix}</div></td>
                <td className="num">{formatNumber(a.requests)}</td>
                <td className="num">{formatNumber(a.status_4xx)}</td>
                <td className="num">{formatNumber(a.status_5xx)}</td>
                <td className="num">{formatRatio(a.error_rate_5xx, 2)}</td>
                <td className="num" title={a.p95_capped ? 'At or above the histogram\'s top bucket — the true p95 may be higher.' : undefined}>
                  {a.p95_ms === null ? '—' : `${a.p95_capped ? '≥ ' : ''}${formatMs(a.p95_ms)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function valueText(v: BasisValue): string {
  if (v.value === null) return '—';
  switch (v.unit) {
    case 'ratio':
      return formatRatio(v.value, 2);
    case 'bytes':
      return formatBytes(v.value);
    case 'cores':
      return `${v.value.toFixed(2)} cores`;
    case 'ms':
      return formatMs(v.value);
    default:
      return `${formatNumber(v.value, 2)} ${v.unit}`;
  }
}

/** One attributed figure: the value, its unit, and the basis badge — ALWAYS the badge (CP3). */
export function BasisFigure({ label, value, whyMissing }: { label: string; value: BasisValue; whyMissing: string }) {
  return (
    <div className="card stat-tile" data-basis={value.basis}>
      <div className="stat-label t-label basis-label"><span>{label}</span><BasisBadge basis={value.basis} /></div>
      <div className="t-heading tabular">{value.value === null ? <Gap why={whyMissing} /> : valueText(value)}</div>
      {value.value === null && <div className="stat-sub t-caption">{whyMissing}</div>}
    </div>
  );
}

export function ComputeSharePanel({ share }: { share: ComputeShare }) {
  const inputs = Object.entries(share.inputs) as [string, BasisValue][];
  return (
    <Section title="Compute share" actions={<BasisBadge basis={share.basis} />}
      meta="attributed from shared containers — never a measurement of this tenant alone">
      <div className="kpi-row">
        <BasisFigure label="CPU (both tiers)" value={share.cpu_cores}
          whyMissing="Needs both the app tier and the DB tier; one is not known for this period." />
        <BasisFigure label="Memory (both tiers)" value={share.memory_bytes}
          whyMissing="Needs both the app tier and the DB tier; one is not known for this period." />
        <BasisFigure label="App-tier CPU" value={share.app_tier_cpu_cores} whyMissing="No request share or no app-tier samples." />
        <BasisFigure label="DB-tier CPU" value={share.db_tier_cpu_cores} whyMissing="No DB active-time share or no DB-tier samples." />
        <BasisFigure label="Request share" value={share.request_share} whyMissing="The gateway scrape delivered no traffic." />
        <BasisFigure label="DB active-time share" value={share.db_active_time_share} whyMissing="No pg_stat_database activity collected." />
      </div>
      <p className="t-caption muted" style={{ margin: '12px 0 0' }}>Method: {share.method}</p>
      <details className="viz-table">
        <summary>Measured inputs</summary>
        <table className="table">
          <tbody>
            {inputs.map(([name, v]) => (
              <tr key={name}>
                <td className="mono t-caption">{name}</td>
                <td className="num">{v.value === null ? <Gap why="Not collected" /> : valueText(v)}</td>
                <td><BasisBadge basis={v.basis} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </Section>
  );
}

/** A non-ok live panel, per its own status (each product degrades on its own — CP2). */
export function PanelProblem({ panel, env, what = 'usage read' }: { panel: LivePanel; env: string; what?: string }) {
  if (panel.status === 'unavailable' || panel.status === 'not_configured') {
    return (
      <UnavailablePanel
        service={panel.service}
        environment={env}
        reason={panel.reason ?? (panel.status === 'not_configured' ? 'service_not_configured' : null)}
        upstreamStatus={panel.http_status ?? null}
        upstreamCode={panel.error_code ?? null}
      />
    );
  }
  const known = describeUpstreamCode(panel.error_code);
  if (known && !known.outage) {
    // The service answered and has nothing for this tenant — a fact, not a failure.
    return (
      <div className="callout callout-info" role="status">
        <span className="callout-icon" aria-hidden="true">i</span>
        <div className="t-body">
          <strong className="t-heading" style={{ display: 'block' }}>{known.title}</strong>
          {known.message}
        </div>
      </div>
    );
  }
  return (
    <div className="callout callout-error" role="status">
      <span className="callout-icon" aria-hidden="true">✕</span>
      <div className="t-body">
        <strong className="t-heading" style={{ display: 'block' }}>
          {known?.title ?? `${panel.service} refused the ${what}${panel.http_status ? ` (${panel.http_status})` : ''}`}
        </strong>
        {known && <div>{known.message}</div>}
        {panel.error_code && <CodeChip code={panel.error_code} />}
        {panel.reason === 'endpoint_or_tenant_not_found'
          ? 'The service has no usage for this tenant, or does not expose the endpoint yet.'
          : 'No figure is shown in its place.'}
      </div>
    </div>
  );
}

function ProductsPanel({ env, tenantId }: { env: string; tenantId: string }) {
  const [period, setPeriod] = useState(currentPeriod());
  const products = useQuery({
    queryKey: ['ops', 'products', env, tenantId, period],
    queryFn: () => opsApi.tenants.products(env, tenantId, period),
    ...LIVE,
  });
  const fallbackScale = useCreditScale(env);

  return (
    <Section
      title="Products"
      actions={<><BasisBadge basis="measured" /><MonthSelect value={period} onChange={setPeriod} label="Period" /></>}
      meta="LIVE from billing, AI and messaging — each panel on its own"
    >
      <QueryState query={products} service="billing">
        {(data) => <ProductsBody data={data} env={env} fallbackScale={fallbackScale.scale} />}
      </QueryState>
    </Section>
  );
}

function ProductsBody({ data, env, fallbackScale }: { data: Products; env: string; fallbackScale: number | null }) {
  // billing's scale from the same answer; a null there is UNKNOWN, so the live settings read is tried — never a 100.
  const scale = (data.billing.status === 'ok' && data.billing.data ? data.billing.data.credit_display_scale : null) ?? fallbackScale;

  const messagingByPurpose = useMemo(() => {
    const rows = data.messaging.status === 'ok' ? data.messaging.data?.rows ?? [] : [];
    const map = new Map<string, { messages: number; parts: number; delivered: number; failed: number; price: number; cost: number }>();
    for (const r of rows) {
      const acc = map.get(r.purpose) ?? { messages: 0, parts: 0, delivered: 0, failed: 0, price: 0, cost: 0 };
      acc.messages += r.messages;
      acc.parts += r.parts;
      acc.delivered += r.delivered;
      acc.failed += r.failed;
      acc.price += r.price_credits;
      acc.cost += r.cost_credits;
      map.set(r.purpose, acc);
    }
    return [...map.entries()];
  }, [data.messaging]);

  return (
    <div className="stack">
      <div>
        <h3 className="t-heading" style={{ margin: '0 0 8px' }}>Billing — rated usage</h3>
        {data.billing.status === 'ok' && data.billing.data
          ? <RatedLinesTable usage={data.billing.data} caption="Rated usage this period" />
          : <PanelProblem panel={data.billing} env={env} />}
      </div>

      <div>
        <h3 className="t-heading" style={{ margin: '0 0 8px' }}>AI credits by feature</h3>
        {data.ai.status === 'ok' && data.ai.data ? (
          data.ai.data.features.length === 0 ? (
            <div className="empty-state t-body">No AI usage in {data.period}.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Feature</th><th className="num">Calls</th><th className="num">Credits debited</th><th className="num">Provider cost</th></tr></thead>
                <tbody>
                  {data.ai.data.features.map((f) => (
                    <tr key={f.feature}>
                      <td className="mono">{f.feature}</td>
                      <td className="num">{formatNumber(f.calls)}</td>
                      <td className="num"><CreditAmount micro={f.credits_debited} scale={scale} inline /></td>
                      <td className="num">{formatCredits(f.provider_cost_credits, scale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : <PanelProblem panel={data.ai} env={env} />}
      </div>

      <div>
        <h3 className="t-heading" style={{ margin: '0 0 8px' }}>SMS by purpose</h3>
        {data.messaging.status === 'ok' ? (
          messagingByPurpose.length === 0 ? (
            <div className="empty-state t-body">No messages in {data.period}.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Purpose</th><th className="num">Messages</th><th className="num">Parts</th><th className="num">Delivered · failed</th><th className="num">Credits</th><th className="num">Our cost</th></tr></thead>
                <tbody>
                  {messagingByPurpose.map(([purpose, r]) => (
                    <tr key={purpose}>
                      <td className="mono">{purpose}</td>
                      <td className="num">{formatNumber(r.messages)}</td>
                      <td className="num">{formatNumber(r.parts)}</td>
                      <td className="num">{formatNumber(r.delivered)} · {formatNumber(r.failed)}</td>
                      <td className="num"><CreditAmount micro={r.price} scale={scale} inline /></td>
                      <td className="num">{formatCredits(r.cost, scale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : <PanelProblem panel={data.messaging} env={env} />}
      </div>
    </div>
  );
}
